import { useEffect, useCallback } from "react";
import { Platform } from "react-native";

interface UseEscapeKeyOptions {
  /** Whether the escape key handler is active */
  isActive: boolean;
  /** Callback when Escape key is pressed */
  onEscape: () => void;
  /** Priority level for nested modals (higher priority handles first) */
  priority?: number;
}

// Stack to manage nested escape key handlers
const escapeHandlerStack: {
  priority: number;
  handler: () => void;
  id: number;
}[] = [];
let handlerId = 0;

/**
 * Hook to handle Escape key press for closing modals/dropdowns.
 * Supports nested modals via priority system - highest priority handler executes first.
 *
 * @example
 * // Basic usage
 * useEscapeKey({
 *   isActive: isModalOpen,
 *   onEscape: closeModal,
 * });
 *
 * // Nested modal with higher priority
 * useEscapeKey({
 *   isActive: isConfirmDialogOpen,
 *   onEscape: closeConfirmDialog,
 *   priority: 10, // Higher than parent modal
 * });
 */
export function useEscapeKey({
  isActive,
  onEscape,
  priority = 0,
}: UseEscapeKeyOptions): void {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      // Find the handler with the highest priority
      if (escapeHandlerStack.length === 0) return;

      const highestPriorityHandler = escapeHandlerStack.reduce((prev, curr) =>
        curr.priority > prev.priority ? curr : prev
      );

      // Only the highest priority handler should execute
      if (highestPriorityHandler.handler === onEscape) {
        e.preventDefault();
        e.stopPropagation();
        onEscape();
      }
    },
    [onEscape]
  );

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined" || !isActive) {
      return;
    }

    const currentId = ++handlerId;

    // Add handler to stack
    escapeHandlerStack.push({
      priority,
      handler: onEscape,
      id: currentId,
    });

    document.addEventListener("keydown", handleEscape, true);

    return () => {
      document.removeEventListener("keydown", handleEscape, true);

      // Remove handler from stack
      const index = escapeHandlerStack.findIndex((h) => h.id === currentId);
      if (index > -1) {
        escapeHandlerStack.splice(index, 1);
      }
    };
  }, [isActive, priority, onEscape, handleEscape]);
}

export default useEscapeKey;
